import { Checkbox, Alert, Spin } from "antd";
import React, { useState } from "react";
import {
  PersonalizationValue,
  SendStepsCommon,
  TriggerStepCreate,
} from "@dots-projects/types";
import {
  UseFormReturn,
  Controller,
  ControllerRenderProps,
} from "react-hook-form";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import { FormLabel } from "./FormLabel";
import { getAvailableChannelVariablesChannelsBySteps } from "@dots-projects/types";
import { canStepBeEphemeral } from "./reducer/triggerUtils";
import { CheckboxChangeEvent } from "antd/lib/checkbox/Checkbox";
import { getDiscordChannels } from "../../lib/discordQueries";
import { useQuery } from "react-query";
import discord_channels from "apps/dots-frontend/types/discord_channels";
import TriggerEditorChannelSelector from "./TriggerEditorChannelSelector";
import TriggerEditorDiscordTextEditor from "./TriggerEditorDiscordTextEditor";
import { Input } from "./Input";

export function useGuildChannels(guildId: string) {
  return useQuery<discord_channels[], Error>(
    ["guildChannels", guildId],
    async () => {
      const channels: any[] | null = await getDiscordChannels(guildId);
      if (
        channels &&
        channels.length > 0 &&
        typeof channels === "object" &&
        Array.isArray(channels)
      ) {
        return channels as discord_channels[];
      } else {
        throw new Error(`Failed to fetch channels. guild: ${guildId}`);
      }
    }
  );
}

/**
 * This component is used in components for specific trigger steps where the user has to select a channel, and writes a message as
 * such as StepFormSendSurvey or StepFormSendMessage.
 *
 * It includes common parts such as step type, name, delete and save.
 */
export const SendStepFormWithCommonParts: React.FC<{
  step: TriggerStepCreate;
  form: UseFormReturn<any>;
  insertVariables: PersonalizationValue[];
  ephemeralCheckbox?: boolean;
}> = ({ step, children, form, insertVariables, ephemeralCheckbox = true }) => {
  const { state } = useTriggerEditorStateDispatch();
  const [showEphemeralError, setShowEphemeralError] = useState(false);

  const {
    control,
    watch,
    setValue,
    resetField,
    formState: { errors },
    register,
  } = form;
  const readOnly = !state.trigger.current.isDraft;

  const availableChannels = getAvailableChannelVariablesChannelsBySteps(
    state.trigger.current.isDraft
      ? {
          ...state.trigger.current,
          type: state.trigger.current.liveTriggerConfigs.type,
        }
      : state.trigger.current
  );
  const isEphemeral = watch("ephemeral");
  const showDelayInput = watch("delayed");

  const stepCanBeEphemeral = canStepBeEphemeral(state.trigger.current, step);

  const stepConfig = step.configs as SendStepsCommon;
  const [errorSelect, setErrorSelect] = useState(
    ["Select channel", "$channelId"].includes(stepConfig.channelId)
  );

  const onEphemeralChange = (
    e: CheckboxChangeEvent,
    formField: ControllerRenderProps<any, "ephemeral">
  ) => {
    if (!stepCanBeEphemeral) {
      e.target.checked = false;
      setShowEphemeralError(true);
    }
    formField.onChange(e.target.checked);
    // if ephemeral, set channel id to interaction.channelId if available
    // otherwise, user cannot set channel id and it ends up being un-set
    if (e.target.checked) {
      resetField("channelId");
      if (availableChannels.find((ch) => ch.id === "{{interaction.channelId}}"))
        setValue("channelId", "{{interaction.channelId}}");
    }
  };

  // regex that checks if message field has at least one url
  const messageWithUrl =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/.test(
      watch("message")
    );

  return (
    <>
      {ephemeralCheckbox ? (
        <Controller
          control={control}
          name="ephemeral"
          render={({ field }) => (
            <>
              <Checkbox
                disabled={readOnly}
                checked={field.value}
                onChange={(value) => {
                  onEphemeralChange(value, field);
                }}
              >
                <span className="text-gray-500">Only visible to the user</span>
              </Checkbox>
              {showEphemeralError && (
                <Alert
                  description="Previous step has to be send_survey, send_multiple_select or trigger type is either a button click or slash command."
                  type="error"
                  closable
                  onClose={() => setShowEphemeralError(false)}
                />
              )}
            </>
          )}
        />
      ) : null}
      {!isEphemeral && (
        <Controller
          control={control}
          name="channelId"
          rules={{
            required: !isEphemeral,
            validate: (channelId) => {
              return (
                channelId &&
                channelId !== "Select channel" &&
                channelId !== "$channelId"
              );
            },
          }}
          render={({ field }) => (
            <TriggerEditorChannelSelector
              readOnly={readOnly}
              field={field}
              errorSelect={errorSelect}
              trigger={state.trigger.current}
              step={step}
              showRefreshButton
            />
          )}
        />
      )}
      <FormLabel label="Message" />
      <TriggerEditorDiscordTextEditor
        form={form}
        htmlField="htmlMessage"
        markdownField="message"
        insertVariables={insertVariables}
        defaultValue={stepConfig.message}
        readOnly={readOnly}
      />
      {messageWithUrl && (
        <Controller
          control={control}
          name="removeUrlEmbeds"
          render={({ field }) => (
            <Checkbox
              disabled={readOnly}
              style={{ margin: 0 }}
              checked={field.value}
              onChange={field.onChange}
            >
              <span className="text-gray-500 text-xs">
                You added URL(s)! Do you want to remove all embeds on this
                message?
              </span>
            </Checkbox>
          )}
        />
      )}
      <Controller
        control={control}
        name="delayed"
        render={({ field }) => (
          <>
            <Checkbox
              disabled={readOnly}
              checked={field.value}
              onChange={(e) => {
                if (!e.target.checked) {
                  setValue("delayMinutes", 0);
                }
                field.onChange(e.target.checked);
              }}
            >
              <span className="text-gray-500">
                Check here to add a delay before sending this message
              </span>
            </Checkbox>
          </>
        )}
      />
      {showDelayInput && (
        <FormLabel label="Send messge after">
          <div className="flex w-full	items-end justify-items-center">
            <Input
              style={{ width: "100%" }}
              readOnly={readOnly}
              {...register("delayMinutes", {
                valueAsNumber: true,
                validate: (value) => value >= 0,
              })}
            />
            <p style={{ margin: "0 0 0 15px" }}> Minutes</p>
          </div>
          {errors["delayMinutes"] && (
            <p className="text-red-500 m-0">Minimum value is 0</p>
          )}
        </FormLabel>
      )}
      {children}
    </>
  );
};
